import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, Button, Grid, Divider } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AssessmentIcon from "@mui/icons-material/Assessment";
import PeopleIcon from "@mui/icons-material/People";
import { fetchMemberCategory, deleteMemberCategory, deleteMemberCategoryCriteriaValue, fetchMemberTypes } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import DetailSection from "../../components/common/DetailSection";
import DetailField from "../../components/common/DetailField";
import ConfirmDialog from "../../components/common/ConfirmDialog";

export default function MemberCategoryDetailPage() {
  const { businessDomainId, categoryId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [data, setData] = useState(null);
  const [memberTypeName, setMemberTypeName] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [cat, types] = await Promise.all([
        fetchMemberCategory(categoryId),
        fetchMemberTypes(businessDomainId),
      ]);
      setData(cat);
      const found = (Array.isArray(types) ? types : []).find(
        (mt) => mt.memberTypeId === cat.memberTypeId
      );
      setMemberTypeName(found?.memberTypeName || "");
    } catch (e) { notify.apiError(e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId, businessDomainId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDelete = async () => {
    try {
      // 分類基準値が存在する場合は先にすべて削除（外部キー制約対策）
      const crValues = data.classificationCriteriaValues || [];
      for (const val of crValues) {
        await deleteMemberCategoryCriteriaValue(categoryId, val);
      }
      await deleteMemberCategory(categoryId);
      notify.success(t("message.delete.success"));
      navigate(`/strategy/${businessDomainId}/member-categories`, { replace: true });
    } catch (e) { notify.apiError(e); }
    setConfirmOpen(false);
  };

  if (!data) return null;

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{t("memberCategory.detail.title")}</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/strategy/${businessDomainId}/member-categories/${categoryId}/edit`)}
          >
            {t("common.edit")}
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setConfirmOpen(true)}
          >
            {t("common.delete")}
          </Button>
        </Box>
      </Box>

      <DetailSection>
        <Grid container spacing={2}>
          <Grid size={12} md={6}>
            <DetailField label={t("memberCategory.name")} value={data.memberCategoryName} />
          </Grid>
          <Grid size={12} md={6}>
            <DetailField label={t("memberCategory.memberType")} value={memberTypeName} />
          </Grid>
          <Grid size={12}>
            <DetailField label={t("common.description")} value={data.memberCategoryDescription} />
          </Grid>
        </Grid>
      </DetailSection>

      <Divider sx={{ my: 3 }} />
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <Button
          variant="outlined"
          startIcon={<PeopleIcon />}
          onClick={() => navigate(`/strategy/${businessDomainId}/member-categories/${categoryId}/members`)}
        >
          {t("member.list.title")}
        </Button>
        <Button
          variant="outlined"
          startIcon={<AssessmentIcon />}
          onClick={() => navigate(`/strategy/${businessDomainId}/member-categories/${categoryId}/classification-criteria`)}
        >
          {t("memberCategory.classificationCriteriaValues")}
        </Button>
      </Box>

      <Box sx={{ mt: 3 }}>
        <Button variant="outlined" onClick={() => navigate(`/strategy/${businessDomainId}/member-categories`)}>{t("common.back")}</Button>
      </Box>

      <ConfirmDialog
        open={confirmOpen}
        onConfirm={handleDelete}
        onCancel={() => setConfirmOpen(false)}
      />
    </Box>
  );
}
